/**
 * Panel de catalogo: cuantos productos tiene la tienda y en que estado estan.
 *
 * Todo sale de `GET /api/v1/monitoring/summary`, que cuenta en Postgres con un
 * `GROUP BY` --no pagina ni trae filas para contarlas en el navegador--, asi
 * que los numeros no dependen de ningun filtro de pantalla.
 *
 * Dos decisiones sobre que se muestra y que no:
 *
 * 1. **Los estados sin productos no ocupan una tarjeta.** Hoy los 661 productos
 *    de las dos tiendas estan en `active`: cuatro tarjetas de las cuales tres
 *    dicen 0 hacen que el panel parezca roto. Los estados vacios se nombran en
 *    una linea al pie, que es informacion igual pero no ruido.
 * 2. **"Sin stock" y "sin ml_id" SI se muestran en cero**, porque ahi el cero
 *    es la buena noticia y es lo que se esta comprobando: cero sin stock es
 *    "todo publicable", cero sin ml_id es "todo el catalogo vino de una sync".
 */

import type { ProductCounts } from "./api";
import { formatCount, percentOf } from "./format";
import { Figure, Note, Panel } from "./ui";

/** Como se lee cada estado de `products.status`, y de donde sale el valor. */
const STATUS_LABEL: Readonly<Record<keyof StatusSlice, string>> = {
  active: "Activos",
  paused: "Pausados",
  closed: "Cerrados",
  other_status: "Otros estados",
};

interface StatusSlice {
  readonly active: number;
  readonly paused: number;
  readonly closed: number;
  readonly other_status: number;
}

const STATUS_KEYS: readonly (keyof StatusSlice)[] = [
  "active",
  "paused",
  "closed",
  "other_status",
];

export function CatalogPanel({ counts }: { readonly counts: ProductCounts }) {
  const publishable = counts.total - counts.banned;
  const bannedShare = percentOf(counts.banned, counts.total);

  const present = STATUS_KEYS.filter((key) => counts[key] > 0);
  const absent = STATUS_KEYS.filter((key) => counts[key] === 0);

  return (
    <Panel
      title="Catalogo"
      to="/productos"
      toLabel="Ver catalogo"
      lead={
        <>
          Filas de <code>products</code> de esta tienda. Es lo que hay en la base,
          que puede diferir de MercadoLibre si la sincronizacion quedo vieja (ver
          el panel de abajo).
        </>
      }
    >
      {counts.total === 0 ? (
        <Note tone="warn">
          La tienda no tiene ningun producto en la base. Si deberia tenerlos, lo
          que falta no es esta pantalla: es una sincronizacion desde
          MercadoLibre.
        </Note>
      ) : (
        <>
          <div className="stat-grid">
            <Figure label="Productos" value={formatCount(counts.total)} />
            <Figure
              label="Baneados"
              value={formatCount(counts.banned)}
              tone={counts.banned > 0 ? "warn" : "neutral"}
              hint={
                <>
                  {bannedShare === null ? null : `${bannedShare}% del catalogo. `}
                  Excluidos de la publicacion entre tiendas.
                </>
              }
            />
            <Figure
              label="Publicables"
              value={formatCount(publishable)}
              hint="Productos menos baneados. No descuenta los que ya existen en la otra tienda: esa comparacion no la expone ningun endpoint todavia."
            />
            <Figure
              label="Sin stock"
              value={formatCount(counts.out_of_stock)}
              tone={counts.out_of_stock > 0 ? "warn" : "ok"}
              hint="stock = 0 en la base."
            />
          </div>

          <div className="home-breakdown">
            <span className="home-breakdown__title">Estado de la publicacion en ML</span>
            <ul className="home-breakdown__list">
              {present.map((key) => {
                const share = percentOf(counts[key], counts.total);
                return (
                  <li key={key}>
                    <strong>{formatCount(counts[key])}</strong>{" "}
                    {STATUS_LABEL[key].toLowerCase()}
                    {share === null ? null : ` (${share}%)`}
                  </li>
                );
              })}
            </ul>
            {absent.length === 0 ? null : (
              <Note>
                Sin productos en: {absent.map((key) => STATUS_LABEL[key].toLowerCase()).join(", ")}.
              </Note>
            )}
          </div>

          {counts.without_ml_id > 0 ? (
            <Note tone="warn">
              <strong>{formatCount(counts.without_ml_id)}</strong> productos sin{" "}
              <code>ml_id</code>: no vinieron de una sincronizacion con
              MercadoLibre y no se pueden publicar clonando.
            </Note>
          ) : (
            <Note>
              Todos los productos tienen <code>ml_id</code>: el catalogo entero
              vino de una sincronizacion con MercadoLibre.
            </Note>
          )}
        </>
      )}
    </Panel>
  );
}
